import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase.js';
import Header from '../../components/ui/Header';
import NotificationToast from '../../components/ui/NotificationToast';
import EmailComposer from './components/EmailComposer';
import EmailPreview from './components/EmailPreview';
import ContractDataPanel from './components/ContractDataPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EmailTemplateGenerator = () => {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const [selectedTemplate, setSelectedTemplate] = useState('congratulatory');
  const [emailData, setEmailData] = useState({});
  const [contractData, setContractData] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(true); // Default to preview mode when accessed from deal
  const [isContractPanelOpen, setIsContractPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load contract data and draft snapshot
  useEffect(() => {
    if (dealId) {
      loadDealEmailData();
    } else {
      loadLegacyContractData();
    }
  }, [dealId]);

  const loadDealEmailData = async () => {
    try {
      // Load deal data
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;

      // Load latest draft snapshot
      const { data: draftData, error: draftError } = await supabase
        .from('draft_snapshots')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (draftError) throw draftError;

      setSelectedData(draftData.selected_data);
      
      // Create contract data structure for compatibility
      const contractDataForEmail = {
        extractedData: {
          property: {
            address: `${dealData.property_street}, ${dealData.city}, ${dealData.state} ${dealData.zip}`.trim(),
            purchasePrice: 'Contact agent for price' // This would come from original contract data
          },
          parties: {
            buyer: dealData.buyer_names || 'Not specified',
            seller: dealData.seller_names || 'Not specified'
          },
          dates: {},
          financial: {
            deposits: draftData.selected_data.deposits?.map(d => d.description).join('; ') || 'Not specified'
          },
          nonStandardClauses: draftData.selected_data.additionalTerms?.map(t => t.description) || []
        }
      };

      // Convert milestones to dates format
      if (draftData.selected_data.milestones) {
        draftData.selected_data.milestones.forEach(milestone => {
          const dateKey = milestone.type === 'custom' ? 'customDate' : milestone.type;
          contractDataForEmail.extractedData.dates[dateKey] = {
            value: new Date(milestone.start_dt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            confidence: 90
          };
        });
      }

      setContractData(contractDataForEmail);
      
    } catch (error) {
      console.error('Error loading deal email data:', error);
      addNotification({
        type: 'error',
        title: 'Load Error',
        message: 'Failed to load contract data for email'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLegacyContractData = () => {
    try {
      // Try to load the latest contract data from localStorage (legacy mode)
      const storedData = localStorage.getItem('latestContractData');
      
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.log('📧 Email Generator - Loaded contract data:', parsedData);
        setContractData(parsedData);
      } else {
        // Fallback to demo data if no real data available
        setContractData({
          contractId: 'demo_contract',
          contract_filename: 'Demo_Contract.pdf',
          created_at: new Date().toISOString(),
          status: 'processed',
          extractedData: {
            property: {
              address: '123 Main St, Anytown, CA 90210',
              purchasePrice: '$500,000'
            },
            parties: {
              buyer: 'John Smith',
              seller: 'Jane Doe'
            },
            dates: {
              contractDate: { value: 'January 15, 2024' },
              closingDate: { value: 'February 28, 2024' },
              inspectionDeadline: { value: 'January 25, 2024' },
              financingDeadline: { value: 'February 10, 2024' }
            },
            financial: {
              deposits: '$10,000'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading contract data:', error);
      // Use fallback data
      setContractData({
        contractId: 'demo_contract',
        extractedData: {
          property: { address: 'Demo Property', purchasePrice: '$500,000' },
          parties: { buyer: 'Demo Buyer', seller: 'Demo Seller' },
          dates: {},
          financial: { deposits: '$10,000' }
        }
      });
    }
    setLoading(false);
  };


  // Initialize email data with representation role
  useEffect(() => {
    const representationRole = localStorage.getItem('representationRole') || 'buyer';
    setEmailData({
      representationRole,
      contractData
    });
  }, [contractData]);

  const handleEmailChange = (newEmailData) => {
    setEmailData(newEmailData);
  };

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
  };

  const handleCopyToClipboard = (emailContent) => {
    // Clear any existing notifications first to prevent stacking
    setNotifications([]);
    
    // Add the new notification
    const notification = {
      type: 'success',
      title: 'Email Copied!',
      message: 'Email content has been copied to clipboard and is ready to paste into Gmail.',
      duration: 3000
    };
    
    addNotification(notification);
    
    // Auto-dismiss after the duration to ensure it doesn't persist
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.title !== 'Email Copied!'));
    }, 3100); // Slightly longer than duration to be safe
  };

  const handleTestCalendarLinks = (type) => {
    const messages = {
      google: 'Google Calendar link tested successfully. Link will open in new tab.',
      outlook: 'Outlook Calendar link tested successfully. Link will open in new tab.',
      ics: 'ICS file download initiated. File contains all contract deadlines.'
    };

    addNotification({
      type: 'info',
      title: 'Calendar Link Tested',
      message: messages[type],
      duration: 3000
    });

    // Simulate calendar link actions
    if (type === 'google') {
      window.open('https://calendar.google.com/calendar/render?action=TEMPLATE&text=Contract+Deadlines', '_blank');
    } else if (type === 'outlook') {
      window.open('https://outlook.live.com/calendar/0/deeplink/compose?subject=Contract+Deadlines', '_blank');
    } else if (type === 'ics') {
      // Simulate ICS file download
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ContractParser Pro//EN
BEGIN:VEVENT
SUMMARY:Inspection Deadline
DTSTART:20250125T120000Z
DTEND:20250125T130000Z
DESCRIPTION:Contract inspection deadline for ${contractData?.propertyAddress}
END:VEVENT
END:VCALENDAR`;
      
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contract-deadlines.ics';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleRefreshContractData = () => {
    addNotification({
      type: 'info',
      title: 'Refreshing Contract Data',
      message: 'Contract data is being refreshed from the latest analysis.',
      duration: 2000
    });
    
    // Simulate refresh
    setTimeout(() => {
      setContractData({ ...mockContractData, lastUpdated: new Date().toISOString() });
      addNotification({
        type: 'success',
        title: 'Data Refreshed',
        message: 'Contract data has been updated successfully.',
        duration: 3000
      });
    }, 1000);
  };

  const handleSelectContract = () => {
    addNotification({
      type: 'info',
      title: 'Contract Selection',
      message: 'Navigate to Contract History to select a different contract.',
      action: {
        label: 'Go to History',
        onClick: () => window.location.href = '/contract-history'
      },
      duration: 5000
    });
  };

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const toggleContractPanel = () => {
    setIsContractPanelOpen(!isContractPanelOpen);
  };

  return (
    <>
      <Helmet>
        <title>Email Template Generator - ContractParser Pro</title>
        <meta name="description" content="Generate personalized client communications with embedded contract deadlines and calendar integration for real estate professionals." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => dealId ? navigate(`/contract/${dealId}`) : navigate('/contract-analysis-results')}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200"
                    title="Back to Contract Summary"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </button>
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Mail" size={24} color="var(--color-accent)" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-heading font-bold" style={{color: '#0d1519'}}>
                      Email Template Generator
                    </h1>
                    <p className="text-lg" style={{color: '#6b7280'}}>
                      Create personalized client communications with contract deadlines
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Single Column Layout */}
            <div className="max-w-4xl mx-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
                    <p>Loading email preview...</p>
                  </div>
                </div>
              ) : (
                <EmailPreview
                  emailData={emailData}
                  contractData={contractData}
                  selectedData={selectedData}
                  dealId={dealId}
                  onCopyToClipboard={handleCopyToClipboard}
                  onTestCalendarLinks={handleTestCalendarLinks}
                />
              )}
            </div>

          </div>
        </main>


        {/* Notifications */}
        <NotificationToast
          notifications={notifications}
          onDismiss={dismissNotification}
          maxVisible={3}
        />
      </div>
    </>
  );
};

export default EmailTemplateGenerator;